import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, asItems, formatApiError } from "@/lib/api";
import { ArrowRight, Building2, RefreshCw } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

export default function Agents() {
  const [items, setItems] = useState([]);
  const [city, setCity] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    const params = city ? `?city=${encodeURIComponent(city)}` : "";
    let live = true;
    setLoading(true);
    setError("");
    api.get(`/agents${params}`)
      .then(({ data }) => { if (live) setItems(asItems(data)); })
      .catch((requestError) => {
        if (live) {
          setItems([]);
          setError(formatApiError(requestError));
        }
      })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [city, reloadKey]);

  return (
    <div className="premium-page-surface bg-transparent min-h-screen" data-testid="agents-page">
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-12">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-10">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-muted-foreground mb-2">Agents</p>
            {/* Brand gradient accent on the lead word */}
            <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter text-primary"><span className="brand-gradient-text">Verified</span> agent profiles</h1>
          </div>
          <Input placeholder="Filter by city…" value={city} onChange={(e) => setCity(e.target.value)} className="md:w-80" data-testid="agents-city-filter"/>
        </div>
        {error && <div className="mb-8 rounded-2xl border border-destructive/30 bg-destructive/5 p-6 text-center" role="alert">
          <p className="font-bold text-primary">Agents could not be loaded</p>
          <p className="mt-1 text-sm text-muted-foreground">{error}</p>
          <Button type="button" variant="outline" className="mt-4" onClick={() => setReloadKey((key) => key + 1)}><RefreshCw className="mr-2 h-4 w-4" />Try again</Button>
        </div>}
        {loading ? <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6" aria-label="Loading agents">
          {[0, 1, 2, 3].map((item) => <div key={item} className="h-72 rounded-2xl border border-border shimmer" />)}
        </div> : !error && <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {items.map((a, i) => (
            <Link key={a.user_id} to={`/agents/${a.user_id}`} style={{ animationDelay: `${Math.min(i, 12) * 60}ms`, animationFillMode: "both" }} className="premium-card premium-card-interactive bg-background border border-border rounded-2xl p-6 hover:-translate-y-1 transition group animate-fade-up" data-testid={`agent-item-${a.user_id}`}>
              <div className="w-20 h-20 rounded-full bg-gradient-to-br from-primary/15 to-secondary overflow-hidden mb-4 ring-2 ring-primary/10 grid place-items-center text-primary font-display font-black text-xl group-hover:scale-105 group-hover:ring-primary/30 transition-all duration-300 animate-gradient-pan">
                {a.picture ? <img src={a.picture} alt={a.name} loading="lazy" decoding="async" className="w-full h-full object-cover"/> : (a.name ? a.name.split(" ").map(n=>n[0]).join("").slice(0,2).toUpperCase() : "SH")}
              </div>
              <p className="font-display font-bold text-primary group-hover:text-primary transition-colors">{a.name}</p>
              <p className="text-xs uppercase tracking-wider text-muted-foreground mt-1">{a.city || "—"}</p>
              <div className="mt-2 flex flex-wrap items-center gap-1.5 text-[11px] font-bold">
                {a.rating ? <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-amber-700">★ {a.rating} ({a.reviews_count})</span> : <span className="rounded-full bg-secondary px-2 py-0.5 text-muted-foreground">New Agent</span>}
                {Number(a.verified_transactions) > 0 && <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-emerald-700">{a.verified_transactions} verified {Number(a.verified_transactions) === 1 ? "deal" : "deals"}</span>}
              </div>
              {a.bio && <p className="text-sm text-muted-foreground mt-3 line-clamp-2">{a.bio}</p>}
              <div className="border-t border-border mt-5 pt-4 flex items-center justify-between text-xs font-bold text-primary">
                <span>{Number(a.property_count || 0).toLocaleString("en-IN")} listings</span>
                <span>{a.experience_years ? `${a.experience_years}y exp` : "Experience unavailable"}</span>
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition"/>
              </div>
            </Link>
          ))}
        </div>}
        {!loading && !error && items.length === 0 && <p className="text-muted-foreground text-center py-20">No agents found.</p>}
      </div>
    </div>
  );
}
